import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import { Retailer } from "./retailerService";

export interface RetailerGroup {
  id: string;
  org_id: string;
  name: string;
  retailer_ids: string[];
  created_at: string;
  updated_at: string;
  retailers?: Retailer[];
  retailer_count?: number;
}

export interface RetailerGroupFormData {
  name: string;
  retailer_ids: string[];
}

/**
 * Get user's organization ID
 */
const getUserOrganizationId = async (): Promise<string | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: orgMember, error } = await supabase
      .from("base_organisation_members")
      .select("organisation_id")
      .eq("user_id", user.id)
      .limit(1)
      .single();

    if (error) {
      console.error("Error getting org ID:", error);
      return null;
    }

    return orgMember?.organisation_id || null;
  } catch (error) {
    console.error("Error getting organization ID:", error);
    return null;
  }
};

/**
 * Get all retailer groups for the user's organization
 */
export const getRetailerGroups = async (): Promise<{
  success: boolean;
  data?: RetailerGroup[];
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data: groups, error: groupsError } = await supabase
      .from("retailer_group")
      .select("*")
      .eq("org_id", orgId)
      .order("name", { ascending: true });

    if (groupsError) throw groupsError;

    if (!groups || groups.length === 0) {
      return { success: true, data: [] };
    }

    // Fetch retailers for each group
    const groupsWithRetailers = await Promise.all(
      groups.map(async (group) => {
        const retailerIds = group.retailer_ids || [];

        if (retailerIds.length === 0) {
          return { ...group, retailers: [], retailer_count: 0 };
        }

        const { data: retailers, error: retailersError } = await supabase
          .from("retailer")
          .select("*")
          .in("id", retailerIds)
          .eq("active", true)
          .order("name", { ascending: true });

        if (retailersError) {
          console.error(`Error fetching retailers for group ${group.id}:`, retailersError);
          return { ...group, retailers: [], retailer_count: 0 };
        }

        return {
          ...group,
          retailers: retailers || [],
          retailer_count: retailers?.length || 0,
        };
      })
    );

    return { success: true, data: groupsWithRetailers };
  } catch (error: any) {
    console.error("Error fetching retailer groups:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single retailer group by ID
 */
export const getRetailerGroupById = async (
  id: string
): Promise<{
  success: boolean;
  data?: RetailerGroup;
  error?: string;
}> => {
  try {
    const { data: group, error: groupError } = await supabase
      .from("retailer_group")
      .select("*")
      .eq("id", id)
      .single();

    if (groupError) throw groupError;

    const retailerIds = group.retailer_ids || [];

    if (retailerIds.length === 0) {
      return { success: true, data: { ...group, retailers: [], retailer_count: 0 } };
    }

    const { data: retailers, error: retailersError } = await supabase
      .from("retailer")
      .select("*")
      .in("id", retailerIds)
      .eq("active", true)
      .order("name", { ascending: true });

    if (retailersError) {
      console.error("Error fetching retailers:", retailersError);
      return { success: true, data: { ...group, retailers: [], retailer_count: 0 } };
    }

    return {
      success: true,
      data: {
        ...group,
        retailers: retailers || [],
        retailer_count: retailers?.length || 0,
      },
    };
  } catch (error: any) {
    console.error("Error fetching retailer group:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a new retailer group
 */
export const createRetailerGroup = async (
  formData: RetailerGroupFormData
): Promise<{
  success: boolean;
  data?: RetailerGroup;
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    if (!formData.name.trim()) {
      return { success: false, error: "Group name is required" };
    }

    if (!formData.retailer_ids || formData.retailer_ids.length === 0) {
      return { success: false, error: "At least one retailer must be selected" };
    }

    // Create the group with retailer IDs
    const { data: group, error: groupError } = await supabase
      .from("retailer_group")
      .insert({
        org_id: orgId,
        name: formData.name.trim(),
        retailer_ids: formData.retailer_ids,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (groupError) throw groupError;

    // Fetch the created group with retailers
    return await getRetailerGroupById(group.id);
  } catch (error: any) {
    console.error("Error creating retailer group:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing retailer group
 */
export const updateRetailerGroup = async (
  id: string,
  formData: Partial<RetailerGroupFormData>
): Promise<{
  success: boolean;
  data?: RetailerGroup;
  error?: string;
}> => {
  try {
    const updateData: any = {
      updated_at: new Date().toISOString(),
    };

    if (formData.name !== undefined) {
      if (!formData.name.trim()) {
        return { success: false, error: "Group name is required" };
      }
      updateData.name = formData.name.trim();
    }

    if (formData.retailer_ids !== undefined) {
      if (formData.retailer_ids.length === 0) {
        return { success: false, error: "At least one retailer must be selected" };
      }
      updateData.retailer_ids = formData.retailer_ids;
    }

    // Update the group
    const { error: updateError } = await supabase
      .from("retailer_group")
      .update(updateData)
      .eq("id", id);

    if (updateError) throw updateError;

    // Fetch the updated group with retailers
    return await getRetailerGroupById(id);
  } catch (error: any) {
    console.error("Error updating retailer group:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a retailer group
 */
export const deleteRetailerGroup = async (
  id: string
): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase.from("retailer_group").delete().eq("id", id);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error("Error deleting retailer group:", error);
    return { success: false, error: error.message };
  }
};

